// 分组 API
import { supabase } from './client';
import type {
  Group,
  GroupMember,
  GroupMemberWithProfile,
  GroupInvitation,
  GroupServiceError,
  InvitationStatus,
  InviteMemberCallbacks,
  AcceptInvitationCallbacks,
} from '../types';

/**
 * 获取用户的分组列表
 */
export async function getUserGroups(): Promise<{ groups: Group[]; error: GroupServiceError | null }> {
  if (!supabase) {
    return { groups: [], error: { message: 'Supabase 未配置' } };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { groups: [], error: { message: '用户未登录' } };
  }

  try {
    const { data, error } = await supabase
      .from('group_members')
      .select(`
        group_id,
        groups (
          id,
          name,
          created_by,
          created_at,
          updated_at,
          is_personal,
          is_deletable,
          color
        )
      `)
      .eq('user_id', user.id);

    if (error) {
      return { groups: [], error: { message: error.message, code: error.code } };
    }

    const groups: Group[] = (data || [])
      .map((item: any) => ({
        id: item.groups.id,
        name: item.groups.name,
        createdBy: item.groups.created_by,
        createdAt: item.groups.created_at,
        updatedAt: item.groups.updated_at,
        isPersonal: item.groups.is_personal,
        isDeletable: item.groups.is_deletable,
        color: item.groups.color,
      }));

    return { groups, error: null };
  } catch (error: any) {
    return { groups: [], error: { message: error.message || '获取分组列表失败' } };
  }
}

/**
 * 创建分组
 */
export async function createGroup(name: string, color?: string): Promise<{ group: Group | null; error: GroupServiceError | null }> {
  if (!supabase) {
    return { group: null, error: { message: 'Supabase 未配置' } };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { group: null, error: { message: '用户未登录' } };
  }

  try {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('groups')
      .insert({
        name,
        created_by: user.id,
        created_at: now,
        updated_at: now,
        is_personal: false,
        is_deletable: true,
        color: color || null,
      })
      .select()
      .single();

    if (error) {
      return { group: null, error: { message: error.message, code: error.code } };
    }

    // 自动将创建者添加为成员
    await supabase
      .from('group_members')
      .insert({
        group_id: data.id,
        user_id: user.id,
        role: 'owner',
        joined_at: now,
      });

    const group: Group = {
      id: data.id,
      name: data.name,
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      isPersonal: data.is_personal,
      isDeletable: data.is_deletable,
      color: data.color,
    };

    return { group, error: null };
  } catch (error: any) {
    return { group: null, error: { message: error.message || '创建分组失败' } };
  }
}

/**
 * 获取分组成员
 */
export async function getGroupMembers(groupId: string): Promise<{ members: GroupMemberWithProfile[]; error: GroupServiceError | null }> {
  if (!supabase) {
    return { members: [], error: { message: 'Supabase 未配置' } };
  }

  try {
    const { data, error } = await supabase
      .from('group_members')
      .select(`
        id,
        group_id,
        user_id,
        role,
        joined_at,
        profiles (
          username
        )
      `)
      .eq('group_id', groupId);

    if (error) {
      return { members: [], error: { message: error.message, code: error.code } };
    }

    // 获取用户邮箱
    const userIds = (data || []).map((m: any) => m.user_id);
    const { data: emailsData } = await supabase
      .rpc('get_user_emails_by_ids', { user_ids: userIds });

    const emailMap = new Map<string, string>(
      (emailsData || []).map((e: any) => [e.user_id, e.email || ''])
    );

    const members: GroupMemberWithProfile[] = (data || []).map((item: any) => ({
      id: item.id,
      groupId: item.group_id,
      userId: item.user_id,
      role: item.role,
      joinedAt: item.joined_at,
      username: item.profiles?.username || '',
      email: emailMap.get(item.user_id) || '',
    }));

    return { members, error: null };
  } catch (error: any) {
    return { members: [], error: { message: error.message || '获取分组成员失败' } };
  }
}

/**
 * 邀请成员（通过邮箱）
 * 创建邀请记录，不直接添加成员，需要被邀请者同意
 */
export async function inviteMember(
  groupId: string,
  email: string,
  options?: InviteMemberCallbacks
): Promise<{ invitation: GroupInvitation | null; error: GroupServiceError | null }> {
  if (!supabase) {
    return { invitation: null, error: { message: 'Supabase 未配置' } };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { invitation: null, error: { message: '用户未登录' } };
  }

  try {
    const { members, error: membersError } = await getGroupMembers(groupId);
    if (membersError) {
      return { invitation: null, error: membersError };
    }
    const currentMember = members.find((m) => m.userId === user.id);
    if (!currentMember) {
      return { invitation: null, error: { message: '您不是该分组的成员' } };
    }
    if (currentMember.role !== 'owner' && currentMember.role !== 'admin') {
      return { invitation: null, error: { message: '只有创建者和管理员可以邀请成员' } };
    }

    const cleanEmail = email.trim().toLowerCase();
    const { data: invitedUserId, error: userIdError } = await supabase.rpc('get_user_id_by_email', {
      user_email: cleanEmail,
    });

    if (userIdError || !invitedUserId) {
      return {
        invitation: null,
        error: {
          message: '未找到该邮箱对应的用户，请确保用户已注册',
          code: userIdError?.code,
        },
      };
    }

    const { data: existingMember } = await supabase
      .from('group_members')
      .select('id')
      .eq('group_id', groupId)
      .eq('user_id', invitedUserId)
      .single();

    if (existingMember) {
      return { invitation: null, error: { message: '该用户已经是分组成员' } };
    }

    const { data: existingInvitation } = await supabase
      .from('group_invitations')
      .select('id')
      .eq('group_id', groupId)
      .eq('invitee_id', invitedUserId)
      .eq('status', 'pending')
      .single();

    if (existingInvitation) {
      return { invitation: null, error: { message: '该用户已有待处理的邀请' } };
    }

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    const { data: invitationData, error: inviteError } = await supabase
      .from('group_invitations')
      .insert({
        group_id: groupId,
        inviter_id: user.id,
        invitee_id: invitedUserId,
        status: 'pending',
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single();

    if (inviteError) {
      if (inviteError.code === '42P01' || inviteError.message?.includes('does not exist')) {
        return {
          invitation: null,
          error: {
            message: 'group_invitations 表不存在，请执行数据库迁移脚本',
            code: inviteError.code,
          },
        };
      }
      return {
        invitation: null,
        error: { message: inviteError.message || '创建邀请失败', code: inviteError.code },
      };
    }

    const { data: groupData } = await supabase
      .from('groups')
      .select('name')
      .eq('id', groupId)
      .single();
    const groupName = groupData?.name || '分组';

    const { data: inviterProfile } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', user.id)
      .single();
    const inviterName = inviterProfile?.username || '用户';

    const invitation: GroupInvitation = {
      id: invitationData.id,
      groupId: invitationData.group_id,
      inviterId: invitationData.inviter_id,
      inviteeId: invitationData.invitee_id,
      status: invitationData.status as InvitationStatus,
      createdAt: invitationData.created_at,
      expiresAt: invitationData.expires_at,
      groupName,
      inviterName,
    };

    try {
      await options?.onInviteCreated?.({ invitation, groupName, inviterName });
    } catch (cbError) {
      console.error('[shared/groups] onInviteCreated callback failed:', cbError);
    }

    return { invitation, error: null };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : '邀请成员时发生未知错误';
    return { invitation: null, error: { message: msg } };
  }
}

/**
 * 接受邀请
 */
export async function acceptInvitation(
  invitationId: string,
  options?: AcceptInvitationCallbacks
): Promise<{ member: GroupMemberWithProfile | null; error: GroupServiceError | null }> {
  if (!supabase) {
    return { member: null, error: { message: 'Supabase 未配置' } };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { member: null, error: { message: '用户未登录' } };
  }

  try {
    const { data: invitation, error: inviteError } = await supabase
      .from('group_invitations')
      .select('*')
      .eq('id', invitationId)
      .eq('invitee_id', user.id)
      .eq('status', 'pending')
      .single();

    if (inviteError || !invitation) {
      return {
        member: null,
        error: { message: '邀请不存在或已处理', code: inviteError?.code },
      };
    }

    if (new Date(invitation.expires_at) < new Date()) {
      await supabase
        .from('group_invitations')
        .update({ status: 'expired' })
        .eq('id', invitationId);
      return { member: null, error: { message: '邀请已过期' } };
    }

    const { data: existingMember } = await supabase
      .from('group_members')
      .select('id')
      .eq('group_id', invitation.group_id)
      .eq('user_id', user.id)
      .single();

    if (existingMember) {
      await supabase
        .from('group_invitations')
        .update({ status: 'accepted' })
        .eq('id', invitationId);
      return { member: null, error: { message: '您已经是该分组的成员' } };
    }

    const { data: memberData, error: memberError } = await supabase
      .from('group_members')
      .insert({
        group_id: invitation.group_id,
        user_id: user.id,
        role: 'member',
        joined_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (memberError) {
      return {
        member: null,
        error: {
          message: memberError.message || '添加成员失败',
          code: memberError.code,
        },
      };
    }

    await supabase
      .from('group_invitations')
      .update({ status: 'accepted' })
      .eq('id', invitationId);

    const { data: groupData } = await supabase
      .from('groups')
      .select('name')
      .eq('id', invitation.group_id)
      .single();
    const groupName = groupData?.name || '分组';

    const { data: inviteeProfile } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', user.id)
      .single();
    const inviteeName = inviteeProfile?.username || '用户';

    try {
      await options?.onAccepted?.({
        invitation: {
          group_id: invitation.group_id,
          inviter_id: invitation.inviter_id,
          invitee_id: invitation.invitee_id,
        },
        groupName,
        inviteeName,
      });
    } catch (cbError) {
      console.error('[shared/groups] onAccepted callback failed:', cbError);
    }

    const { members } = await getGroupMembers(invitation.group_id);
    const newMember = members.find((m) => m.id === memberData.id);
    return { member: newMember || null, error: null };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : '接受邀请时发生未知错误';
    return { member: null, error: { message: msg } };
  }
}

/**
 * 拒绝邀请
 */
export async function rejectInvitation(invitationId: string): Promise<{ error: GroupServiceError | null }> {
  if (!supabase) {
    return { error: { message: 'Supabase 未配置' } };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { error: { message: '用户未登录' } };
  }

  try {
    const { error } = await supabase
      .from('group_invitations')
      .update({ status: 'rejected' })
      .eq('id', invitationId)
      .eq('invitee_id', user.id)
      .eq('status', 'pending');

    if (error) {
      return { error: { message: error.message || '拒绝邀请失败', code: error.code } };
    }
    return { error: null };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : '拒绝邀请时发生未知错误';
    return { error: { message: msg } };
  }
}

/**
 * 获取指定分组的所有邀请记录（含待处理、已接受、已拒绝）
 * 仅分组成员可查看
 */
export async function getGroupInvitations(groupId: string): Promise<{
  invitations: GroupInvitation[];
  error: GroupServiceError | null;
}> {
  if (!supabase) {
    return { invitations: [], error: { message: 'Supabase 未配置' } };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { invitations: [], error: { message: '用户未登录' } };
  }

  try {
    const { members, error: membersError } = await getGroupMembers(groupId);
    if (membersError) {
      return { invitations: [], error: membersError };
    }
    const currentMember = members.find((m) => m.userId === user.id);
    if (!currentMember) {
      return { invitations: [], error: { message: '您不是该分组的成员' } };
    }

    const { data, error } = await supabase
      .from('group_invitations')
      .select(`
        *,
        invitee:profiles!invitee_id (username)
      `)
      .eq('group_id', groupId)
      .order('created_at', { ascending: false });

    if (error) {
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('group_invitations')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });

      if (invitationsError) {
        return {
          invitations: [],
          error: { message: invitationsError.message || '获取分组邀请失败', code: invitationsError.code },
        };
      }

      const inviteeIds = (invitationsData || []).map((item: Record<string, unknown>) => item.invitee_id);
      const { data: profilesData } = inviteeIds.length > 0
        ? await supabase.from('profiles').select('id, username').in('id', inviteeIds)
        : { data: [] };

      const profileMap = new Map(
        (profilesData || []).map((p: { id: string; username?: string }) => [p.id, p.username || ''])
      );

      const invitations: GroupInvitation[] = (invitationsData || []).map((item: Record<string, unknown>) => ({
        id: item.id as string,
        groupId: item.group_id as string,
        inviterId: item.inviter_id as string,
        inviteeId: item.invitee_id as string,
        status: item.status as InvitationStatus,
        createdAt: item.created_at as string,
        expiresAt: item.expires_at as string,
        inviteeName: profileMap.get(item.invitee_id as string) || '',
      }));

      return { invitations, error: null };
    }

    const invitations: GroupInvitation[] = (data || []).map((item: Record<string, unknown>) => {
      const invitee = item.invitee as { username?: string } | null;
      return {
        id: item.id,
        groupId: item.group_id,
        inviterId: item.inviter_id,
        inviteeId: item.invitee_id,
        status: item.status as InvitationStatus,
        createdAt: item.created_at,
        expiresAt: item.expires_at,
        inviteeName: invitee?.username || '',
      } as GroupInvitation;
    });

    return { invitations, error: null };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : '获取分组邀请失败';
    return { invitations: [], error: { message: msg } };
  }
}

/**
 * 获取待处理的邀请列表（当前用户收到的）
 */
export async function getPendingInvitations(): Promise<{
  invitations: GroupInvitation[];
  error: GroupServiceError | null;
}> {
  if (!supabase) {
    return { invitations: [], error: { message: 'Supabase 未配置' } };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { invitations: [], error: { message: '用户未登录' } };
  }

  try {
    const { data, error } = await supabase
      .from('group_invitations')
      .select(`
        *,
        groups (name),
        inviter:profiles!inviter_id (username)
      `)
      .eq('invitee_id', user.id)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('group_invitations')
        .select('*')
        .eq('invitee_id', user.id)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (invitationsError) {
        return {
          invitations: [],
          error: { message: invitationsError.message || '获取待处理邀请失败', code: invitationsError.code },
        };
      }

      const client = supabase!;
      const invitationsWithDetails: GroupInvitation[] = await Promise.all(
        (invitationsData || []).map(async (item: Record<string, unknown>) => {
          const { data: groupData } = await client
            .from('groups')
            .select('name')
            .eq('id', item.group_id)
            .single();
          const { data: inviterData } = await client
            .from('profiles')
            .select('username')
            .eq('id', item.inviter_id)
            .single();
          return {
            id: item.id,
            groupId: item.group_id,
            inviterId: item.inviter_id,
            inviteeId: item.invitee_id,
            status: item.status as InvitationStatus,
            createdAt: item.created_at,
            expiresAt: item.expires_at,
            groupName: groupData?.name || '',
            inviterName: inviterData?.username || '',
          } as GroupInvitation;
        })
      );
      return { invitations: invitationsWithDetails, error: null };
    }

    const invitations: GroupInvitation[] = (data || []).map((item: Record<string, unknown>) => {
      const groups = item.groups as { name?: string } | null;
      const inviter = item.inviter as { username?: string } | null;
      return {
        id: item.id,
        groupId: item.group_id,
        inviterId: item.inviter_id,
        inviteeId: item.invitee_id,
        status: item.status as InvitationStatus,
        createdAt: item.created_at,
        expiresAt: item.expires_at,
        groupName: groups?.name || '',
        inviterName: inviter?.username || '',
      } as GroupInvitation;
    });
    return { invitations, error: null };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : '获取待处理邀请失败';
    return { invitations: [], error: { message: msg } };
  }
}
